// utils/currency/format.js
//
// The one formatter both repos print money with: "$1,234.50", "CA$12.00",
// "¥1,235", "BHD 1.235", "USD 1,234.50", "-€3.00". Always the currency's own
// number of decimals (its ISO exponent), en-US digit grouping, a sign before
// the symbol. Dependency-free and vendored into keepr-web (see registry.js).
//
// NOT Intl.NumberFormat, deliberately. A currency value is formatted on BOTH
// sides of the wire -- the API renders display titles, change-log sentences
// and PDF/XLSX exports; the web renders forms, lists and tiles -- and the two
// must print the same characters. Intl's output is whatever CLDR data the
// runtime ships: Node and each browser disagree on the space after "CHF"
// (U+00A0 vs U+202F vs none), on "CA$" vs "$" under narrowSymbol, and they
// change between releases. A display title saved by the server that no
// longer matches what the list prints is a bug nobody can reproduce, so the
// symbols live in registry.js and the digits are laid out here, pinned by
// the shared vectors.
//
// Display options (element `options.display`):
//   style: 'symbol' (default)  the symbol, narrow when no other currency the
//                              element allows shares it: "$12.50" on a
//                              USD-only element, "CA$12.50" beside USD
//          'code'              the ISO code: "USD 12.50", "CAD 12.50"
// A symbol that ends in a letter ("CHF", "kr", "F CFA") is followed by a
// space; a glyph ("$", "€", "CA$") is not.

'use strict';

const { getCurrency, allowedCodes } = require('./registry');
const { readCurrency } = require('./normalize');
const { decimalTextOf } = require('./parse');

const STYLES = ['symbol', 'code'];
// Latin letters, including the accented ones symbols use (Kč, zł, lei).
const ENDS_IN_LETTER_RE = /[A-Za-zÀ-ɏ]$/;

function groupInteger(digits) {
	return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * An integer amount of minor units as plain major-unit text, exact and
 * ungrouped: (123450, 'USD') -> "1234.50", (-5, 'USD') -> "-0.05",
 * (1235, 'JPY') -> "1235", (1235, 'BHD') -> "1.235". '' for an unknown code
 * or a non-integer amount. This is the spelling inputs, KQL literals and
 * spreadsheets read back.
 */
function majorText(amount, code) {
	const currency = getCurrency(code);
	if (!currency || typeof amount !== 'number' || !Number.isSafeInteger(amount)) return '';
	const digits = String(Math.abs(amount));
	const exponent = currency.exponent;
	const sign = amount < 0 ? '-' : '';
	if (!exponent) return sign + digits;
	const padded = digits.length > exponent ? digits : '0'.repeat(exponent - digits.length + 1) + digits;
	return sign + padded.slice(0, padded.length - exponent) + '.' + padded.slice(padded.length - exponent);
}

/** An amount of minor units as a major-unit number (12.5 for 1250 USD); NaN when unreadable. */
function toMajor(amount, code) {
	const text = majorText(amount, code);
	return text ? Number(text) : NaN;
}

/**
 * The symbol a currency prints with on an element. `options.currencies` is
 * the context: the narrow glyph ("$", "kr") when no OTHER allowed currency
 * shares it, the unambiguous symbol ("CA$", "SEK") otherwise -- and the
 * unambiguous one when there is no element context at all.
 */
function symbolFor(code, options) {
	const currency = getCurrency(code);
	if (!currency) return '';
	const allowed = allowedCodes(options);
	if (!allowed.length) return currency.symbol;
	const clash = allowed.some(other => {
		if (other === currency.code) return false;
		const peer = getCurrency(other);
		return peer.narrow === currency.narrow || peer.symbol === currency.narrow;
	});
	return clash ? currency.symbol : currency.narrow;
}

function styleOf(options, prefs) {
	const wanted = prefs && typeof prefs.style === 'string' ? prefs.style
		: (options && options.display && typeof options.display.style === 'string' ? options.display.style : 'symbol');
	return STYLES.indexOf(wanted) === -1 ? 'symbol' : wanted;
}

function withPrefix(prefix, digits, negative) {
	const gap = ENDS_IN_LETTER_RE.test(prefix) ? ' ' : '';
	return (negative ? '-' : '') + prefix + gap + digits;
}

/**
 * formatAmount(amount, code, options, prefs) -> "$1,234.50" ('' when unreadable).
 * `amount` is an integer of minor units; `options` the element options (the
 * allowlist decides the symbol, `display.style` the style); `prefs` an
 * optional { style } override.
 */
function formatAmount(amount, code, options, prefs) {
	const text = majorText(amount, code);
	if (!text) return '';
	const negative = text.charAt(0) === '-';
	const unsigned = negative ? text.slice(1) : text;
	const dot = unsigned.indexOf('.');
	const grouped = dot === -1 ? groupInteger(unsigned) : groupInteger(unsigned.slice(0, dot)) + unsigned.slice(dot);
	const style = styleOf(options, prefs);
	const prefix = style === 'code' ? code : symbolFor(code, options);
	return withPrefix(prefix, grouped, negative && amount !== 0);
}

/**
 * formatCurrency(stored, options, prefs) -> string ('' when empty).
 * `stored` is the item value ({ amount, currency }, a bare number, or a
 * parseable string -- readCurrency's tolerance); `options` the element
 * options; `prefs` an optional { style }.
 */
function formatCurrency(stored, options, prefs) {
	const value = readCurrency(stored, options || {});
	if (!value) return '';
	return formatAmount(value.amount, value.currency, options || {}, prefs);
}

/**
 * formatMajor(n, code, prefs) -> "$12.50" for a plain NUMBER in major units:
 * the display-template `currency(code)` formatter on a number element, so it
 * prints exactly what a currency element holding the same money prints.
 * Rounded half away from zero to the currency's exponent; '' for an unknown
 * code or a non-finite number.
 */
function formatMajor(n, code, prefs) {
	const currency = getCurrency(typeof code === 'string' ? code.trim().toUpperCase() : code);
	if (!currency || typeof n !== 'number' || !Number.isFinite(n)) return '';
	// Rounded on decimal text (parse.js) so 1.005 is $1.01, not binary 1.00.
	const text = decimalTextOf(n);
	const m = text && text.match(/^(-?)(\d+)(?:\.(\d*))?$/);
	if (!m) return '';
	const fraction = m[3] || '';
	const kept = (fraction.slice(0, currency.exponent) + '0'.repeat(currency.exponent)).slice(0, currency.exponent);
	let amount = Number((m[2] + kept).replace(/^0+(?=\d)/, ''));
	if (fraction.charAt(currency.exponent) >= '5') amount += 1;
	if (!Number.isSafeInteger(amount)) return '';
	return formatAmount(m[1] === '-' && amount !== 0 ? -amount : amount, currency.code, null, prefs);
}

module.exports = {
	STYLES,
	majorText,
	toMajor,
	symbolFor,
	formatAmount,
	formatCurrency,
	formatMajor,
};
